import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, ArrowLeft, Plus, Trash, Users, Lock, GlobeSimple, BookmarksSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';

export default function Collections() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('public');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', is_public: true });
  const [tabInitialized, setTabInitialized] = useState(false);

  // Once auth resolves, default a signed-in user to 'mine'
  useEffect(() => {
    if (authLoading || tabInitialized) return;
    setTab(user ? 'mine' : 'public');
    setTabInitialized(true);
  }, [user, authLoading, tabInitialized]);

  useEffect(() => {
    if (authLoading || !tabInitialized) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user, authLoading, tabInitialized]);

  const load = async () => {
    try {
      setLoading(true);
      const endpoint = tab === 'mine' ? '/api/collections/me' : '/api/collections/public';
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        withCredentials: tab === 'mine'
      });
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const createCollection = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      setCreating(true);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/collections`, form, {
        withCredentials: true
      });
      toast.success('Collection created');
      setForm({ name: '', description: '', is_public: true });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error('Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  const deleteCol = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/collections/${id}`, {
        withCredentials: true
      });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <Link to="/" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </Link>

          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <BookmarksSimple size={40} weight="duotone" className="text-primary" />
                <h2 className="text-4xl md:text-5xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                  Reading Lists
                </h2>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Curated collections of articles and classical works. Build your own reading journeys or explore what the community has organized.
              </p>
            </div>
            {user && tab === 'mine' && (
              <button
                onClick={() => setShowForm(!showForm)}
                data-testid="new-collection-button"
                className="bg-primary text-primary-foreground rounded-full py-2 px-5 font-semibold hover:opacity-90 inline-flex items-center gap-2"
              >
                <Plus size={18} /> New Collection
              </button>
            )}
          </div>

          {showForm && (
            <form onSubmit={createCollection} className="bg-card border border-border rounded-lg p-5 mb-6" data-testid="new-collection-form">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Collection name (e.g., Monsoon Poetry)"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary mb-3"
                required
                data-testid="col-name-input"
              />
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description (optional)"
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary mb-3"
                data-testid="col-desc-input"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                    data-testid="col-public-checkbox"
                  />
                  Public
                </label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full border border-border hover:border-primary text-sm">
                    Cancel
                  </button>
                  <button type="submit" disabled={creating} data-testid="submit-collection-form" className="bg-primary text-primary-foreground rounded-full py-2 px-6 font-semibold text-sm disabled:opacity-50">
                    {creating ? 'Creating…' : 'Create'}
                  </button>
                </div>
              </div>
            </form>
          )}

          <div className="flex gap-3 mb-8">
            <button
              onClick={() => setTab('mine')}
              disabled={!user}
              data-testid="tab-my-collections"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'mine' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'} disabled:opacity-50`}
            >
              My Collections
            </button>
            <button
              onClick={() => setTab('public')}
              data-testid="tab-public-collections"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === 'public' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary'}`}
            >
              Community
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="empty-collections">
              <BookmarksSimple size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {tab === 'mine' ? "You haven't created any collections yet." : 'No public collections yet.'}
              </p>
              {tab === 'mine' && !showForm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="text-primary hover:underline text-sm font-semibold"
                >
                  Create your first collection →
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((col) => (
                <div key={col.collection_id} className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary transition-colors group" data-testid={`collection-${col.collection_id}`}>
                  <Link to={`/collections/${col.collection_id}`} className="block">
                    <div className="aspect-[3/2] bg-muted relative overflow-hidden">
                      {col.cover ? (
                        <img src={col.cover} alt={col.name} className="w-full h-full object-cover" />
                      ) : col.items?.[0]?.cover ? (
                        <img src={col.items[0].cover} alt={col.name} className="w-full h-full object-cover opacity-70" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-background">
                          <BookmarksSimple size={48} weight="duotone" className="text-primary/40" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        {col.is_public ? <GlobeSimple size={12} className="text-muted-foreground" /> : <Lock size={12} className="text-muted-foreground" />}
                        <span className="text-xs uppercase tracking-widest text-muted-foreground">{col.is_public ? 'Public' : 'Private'}</span>
                      </div>
                      <h3 className="text-xl mb-1 line-clamp-1" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
                        {col.name}
                      </h3>
                      {col.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{col.description}</p>}
                      <p className="text-xs text-muted-foreground">
                        {col.items?.length || 0} item{(col.items?.length || 0) !== 1 ? 's' : ''}
                        {tab === 'public' && col.user_name ? ` · by ${col.user_name}` : ''}
                      </p>
                    </div>
                  </Link>
                  {tab === 'mine' && (
                    <div className="px-4 pb-3 flex justify-end">
                      <button
                        onClick={() => deleteCol(col.collection_id)}
                        data-testid={`delete-col-${col.collection_id}`}
                        className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                      >
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
